import { useEffect, useState } from 'react'
import { CloudLicenseForm } from '../../components/CloudLicenseModal'
import { formatExpiry } from '../../config/license'
import type { EdgeServer } from '../../types'

// 발급 후 이 시간이 지나도 Edge 가 활성화하지 않으면 점검 안내를 띄운다
const ACTIVATION_WARN_MS = 10 * 60 * 1000

function formatDateTime(iso?: string) {
  return iso ? new Date(iso).toLocaleString('ko-KR', { hour12: false }) : '-'
}

type StepState = 'done' | 'current' | 'waiting' | 'todo'

function Step({ n, state, title, children }: { n: number; state: StepState; title: string; children?: React.ReactNode }) {
  const circle = {
    done: 'bg-green-500 text-white',
    current: 'bg-blue-600 text-white',
    waiting: 'bg-blue-50 text-blue-600 ring-2 ring-blue-200',
    todo: 'bg-gray-100 text-gray-400',
  }[state]
  return (
    <li className="flex gap-3">
      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${circle}`}>
        {state === 'done' ? '✓'
          : state === 'waiting' ? <span className="inline-block w-3 h-3 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          : n}
      </span>
      <div className="min-w-0 pt-1">
        <p className={`text-sm font-medium ${state === 'todo' ? 'text-gray-400' : 'text-gray-800'}`}>{title}</p>
        {children && <div className="text-xs text-gray-500 mt-1 space-y-0.5">{children}</div>}
      </div>
    </li>
  )
}

/**
 * 등록 미완료 서버(승인 대기·발급 완료) 상세 화면 — 대시보드 연동 전 단계.
 * 승인 대기: 라이센스 발급 폼(발급 권한자만). 발급 완료: Edge 가 라이센스를 받아 활성화(Registered)하길 기다린다.
 * 상위 화면이 서버를 폴링하므로 Registered 가 되면 자동으로 일반 상세로 넘어간다.
 */
export default function ActivationPanel({ server, canIssue, canReject, onReject }: {
  server: EdgeServer
  canIssue: boolean
  canReject: boolean
  onReject: () => void
}) {
  const pending = server.status === 'PendingApproval'
  const issuance = server.licenseIssuance

  // 경과 시간 경고 판정용 — 폴링 사이에도 갱신되도록 30초마다 다시 그린다
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (pending) return
    const t = setInterval(() => setNow(Date.now()), 30000)
    return () => clearInterval(t)
  }, [pending])
  const overdue = !pending && !!issuance && now - new Date(issuance.issuedAt).getTime() > ACTIVATION_WARN_MS

  return (
    <div className="bg-surface rounded-xl border border-dashed border-gray-300 p-6 grid gap-8 md:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
      <div>
        <h3 className="font-semibold text-gray-800">대시보드 연동 대기</h3>
        <p className="text-xs text-gray-400 mt-1">
          라이센스를 발급하면 Edge 에 전달되고, Edge 가 활성화되면 모니터링 화면으로 바뀝니다.
        </p>
        <ol className="mt-5 space-y-4">
          <Step n={1} state="done" title="등록 요청">
            <p>{formatDateTime(server.createdAt)}</p>
            <p className="font-mono text-gray-400">{server.id}</p>
          </Step>
          <Step n={2} state={pending ? 'current' : 'done'} title="라이센스 발급">
            {issuance && !pending && (
              <p>{formatDateTime(issuance.issuedAt)} · 만료 {formatExpiry(issuance.expiresAt)} · Node {issuance.maxNodeCount}</p>
            )}
          </Step>
          <Step n={3} state={pending ? 'todo' : 'waiting'} title={pending ? 'Edge 활성화' : 'Edge 활성화 대기 중'}>
            {!pending && (
              <>
                <p>
                  Edge {server.isOnline ? '온라인' : '오프라인'} · 마지막 통신 {formatDateTime(server.lastHeartbeat)}
                </p>
                {overdue && (
                  <p className="text-orange-600 bg-orange-50 rounded-lg px-3 py-2 mt-2">
                    발급 후 10분이 지났지만 아직 활성화되지 않았습니다. Edge 가 실행 중이고 네트워크에 연결되어 있는지 확인하세요.
                  </p>
                )}
              </>
            )}
          </Step>
        </ol>
      </div>

      {pending && (
        <div className="md:border-l md:border-gray-100 md:pl-8">
          {canIssue ? (
            <>
              <h4 className="text-sm font-semibold text-gray-800 mb-3">Cloud 라이센스 발급</h4>
              {/* 발급 후 서버 쿼리 무효화 → status 가 Issued 로 바뀌며 3단계 대기 표시로 넘어간다 */}
              <CloudLicenseForm serverId={server.id} />
            </>
          ) : (
            <p className="text-sm text-gray-400">라이센스 발급 권한이 있는 사용자가 발급하면 활성화됩니다.</p>
          )}
          {canReject && (
            <button onClick={onReject}
              className="mt-4 text-xs text-red-400 hover:text-red-600">등록 요청 거절</button>
          )}
        </div>
      )}
    </div>
  )
}
